

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Colors from '../global/Colors';
import Fonts from '../global/Fonts';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';

const DropdownWrapper = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  padding: 0;
  margin: 0;
  z-index: 20000;
`;

const StyledSelect = styled(Select)<{ $colorMode: 'light' | 'dark' }>`
  width: 100%;
  background-color: ${Colors.transparent};
  border-radius: 4px;
  outline: 1px solid ${props => props.$colorMode === 'dark' ? Colors.grey7 : Colors.grey3};
  color: ${props => props.$colorMode === 'dark' ? Colors.grey2 : Colors.grey8};
  font-family: ${Fonts.lexendSemiLight.fontFamily};
  font-weight: ${Fonts.lexendSemiLight.fontWeight};
  font-size: inherit;
  line-height: 1.4em;
  padding: 4px 6px 4px 12px;
  box-sizing: border-box;
  cursor: pointer;
  transition: border 0.2s ease-in-out;

  & .MuiSelect-select {
    padding: 0;
    margin: 0;
  }

  & .MuiSelect-icon {
    color: ${props => props.$colorMode === 'dark' ? Colors.grey3 : Colors.grey7};
  }
`;

const StyledMenuItem = styled(MenuItem)<{ $colorMode: 'light' | 'dark' }>`
  background-color: ${Colors.transparent};
  color: ${props => props.$colorMode === 'dark' ? Colors.grey2 : Colors.grey8};
  font-family: ${Fonts.lexendSemiLight.fontFamily};
  font-weight: ${Fonts.lexendSemiLight.fontWeight};
  font-size: inherit;
  line-height: inherit;
  
  &:hover {
    background-color: ${props => props.$colorMode === 'dark' ? Colors.grey8+60 : Colors.grey1+60};
  }
  
  &.Mui-selected {
    background-color: ${props => props.$colorMode === 'dark' ? Colors.grey8 : Colors.grey1} !important;
    // color: ${props => props.$colorMode === 'dark' ? Colors.white : Colors.grey9};
  }
  
  &.Mui-selected:hover {
    background-color: ${props => props.$colorMode === 'dark' ? Colors.grey7+60 : Colors.grey2+60} !important;
  }
  
  &:active {
    background-color: ${props => props.$colorMode === 'dark' ? Colors.grey7 : Colors.grey2};
  }
`;

type OptionType = string | { id: string; value: string };

interface DropdownSimpleProps {
  options: OptionType[];
  initialValue?: any;
  onChange?: (event: any) => void;
  colorMode?: 'light' | 'dark';
}

const DropdownSimple: React.FC<DropdownSimpleProps> = ({
  options,
  initialValue = '',
  onChange,
  colorMode = 'light'
}) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const handleChange = (event: SelectChangeEvent<any>) => {
    setValue(event.target.value);
    if (onChange) {
      onChange(event);
    }
  };

  // Helper function to get the display value for an option
  const getOptionDisplay = (option: OptionType): string => {
    if (typeof option === 'string') {
      return option;
    }
    return option.value;
  };

  // Helper function to get the value for an option
  const getOptionValue = (option: OptionType): string => {
    if (typeof option === 'string') {
      return option;
    }
    return option.id;
  };

  return (
    <DropdownWrapper>
      <StyledSelect
        value={value}
        onChange={handleChange}
        displayEmpty
        $colorMode={colorMode}
        MenuProps={{
          PaperProps: {
            sx: {
              backgroundColor: colorMode === 'dark' ? Colors.grey9 : Colors.white,
              color: colorMode === 'dark' ? Colors.white : Colors.black,
              borderRadius: '4px',
              border: `1px solid ${colorMode === 'dark' ? Colors.grey8 : Colors.grey1}`,
              boxShadow: `0 2px 4px ${colorMode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`,
              width: 'auto', // This ensures the menu width matches the select width
              marginTop: '4px',
              zIndex: 20000
            }
          },
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          // This ensures the menu width matches the select width
          MenuListProps: {
            style: { padding: '0', margin: '0' }
          }
        }}
      >
        {options.map((option, index) => (
          <StyledMenuItem 
            key={index} 
            value={getOptionValue(option)}
            $colorMode={colorMode}
          >
            {getOptionDisplay(option)}
          </StyledMenuItem>
        ))}
      </StyledSelect>
    </DropdownWrapper>
  );
};

export default DropdownSimple;